import "./invoiceView.css";
import "../dashboardPage/dashboard.page.css";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Skeleton } from "antd";
import { Navbar } from "@src/components/navbar";
import { getCustomerPortalInvoiceQuery } from "@src/util/query/portal.query";
import { INVOICE_STATE, formatMoney } from "./invoice.page";
import { downloadServerPdf } from "@src/util/portal/download";

// One invoice as the provider sent it - same document as wfc-public's
// invoiceView.jsx (layout and CSS ported), printable / Save as PDF.

export const InvoiceView = () => {
    const { invoiceId } = useParams();
    const navigate = useNavigate();
    const [customerDetail, setCustomerDetail] = useState(null);
    useEffect(() => {
        const detail = localStorage.getItem("customerDetail");
        if (detail) setCustomerDetail(JSON.parse(detail));
    }, []);

    const { data, loading, error } = useQuery(getCustomerPortalInvoiceQuery, {
        variables: { id: invoiceId },
        fetchPolicy: "network-only",
        skip: !invoiceId,
    });
    const invoice = data?.getCustomerPortalInvoice?.response?.invoice;
    const tenant = data?.getCustomerPortalInvoice?.response?.provider;

    const currency = invoice?.currency || tenant?.invoiceCurrency || "GBP";
    const money = (v) => formatMoney(parseFloat(v || 0), currency);
    const vatRate = invoice?.vatRate ?? tenant?.invoiceVatRate ?? 20;
    const lineItems = invoice?.lineItems || [];
    const totalQty = lineItems.reduce((s, l) => s + (parseFloat(l.qty) || 0), 0);
    // detailed invoices (one line per shift) carry the shift's date/guard/times
    const isDetailed = lineItems.some((l) => l.date || l.guard || l.start);
    const state = INVOICE_STATE[invoice?.state] || INVOICE_STATE.outstanding;

    return (
        <div className="cdb-page">
            <Navbar customerDetail={customerDetail} />

            <div className="invoice-toolbar no-print">
                <button type="button" className="cdb-link" onClick={() => navigate("/customer/invoices")}>‹ All invoices</button>
                {invoice && (
                    <div className="cp-inv-status">
                        <span className="cdb-pill" style={{ color: state.color, borderColor: state.color, background: state.bg }}>{state.label}</span>
                        {invoice.state !== "paid" && invoice.dueDate && <span>Due {invoice.dueDate}</span>}
                        <button type="button" className="cdb-btn" onClick={() => window.print()}>Print</button>
                        {/* built by the server and saved straight to downloads */}
                        <button type="button" className="cdb-btn cdb-btn-primary" onClick={() => downloadServerPdf(`/reports/customer/invoice/${invoiceId}.pdf`, null, `invoice-${invoice.invoiceNo}.pdf`)}>Download PDF</button>
                    </div>
                )}
            </div>

            {loading && !invoice ? (
                <div className="invoice-page"><Skeleton active paragraph={{ rows: 12 }} /></div>
            ) : !invoice ? (
                <div className="cdb-empty">{error ? "This invoice could not be found." : ""}</div>
            ) : (
                <div className="invoice-page">

                    {/* ── Header: logo-left / INVOICE-right ── */}
                    <div className="inv-header">
                        <div className="inv-header-left">
                            {tenant?.logo ? (
                                <img src={tenant.logo} alt="Company Logo" className="inv-logo" />
                            ) : (
                                <div className="inv-logo-placeholder">{(tenant?.name || "C")[0]}</div>
                            )}
                            <div className="inv-company-address">
                                <strong>{tenant?.legalname || tenant?.name}</strong><br />
                                {tenant?.address?.split("\n").map((line, i) => <span key={i}>{line}<br /></span>)}
                                {tenant?.tel && <>T: {tenant.tel}<br /></>}
                                {tenant?.invoiceEmail && <>E: {tenant.invoiceEmail}</>}
                            </div>
                        </div>

                        <div className="inv-header-right">
                            <div className="inv-title">INVOICE</div>
                            <table className="inv-meta-table">
                                <tbody>
                                    <tr><td>Invoice No:</td><td><strong>{invoice.invoiceNo}</strong></td></tr>
                                    <tr><td>Tax Date:</td><td>{invoice.taxDate}</td></tr>
                                    {tenant?.vatNumber && <tr><td>VAT Registration No:</td><td>{tenant.vatNumber}</td></tr>}
                                    <tr><td>Terms:</td><td>{tenant?.paymentTerms || "28 DAYS NET"}</td></tr>
                                    {invoice.dueDate && <tr><td>Due Date:</td><td>{invoice.dueDate}</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ── Invoice To + Bank Details ── */}
                    <div className="inv-mid-row">
                        <div className="inv-to-col">
                            <div className="inv-to-label">Invoice To:</div>
                            <strong>{invoice.customerName}</strong>
                            {invoice.customerAddress && <div style={{ whiteSpace: "pre-line", marginTop: "2px" }}>{invoice.customerAddress}</div>}
                        </div>

                        {(tenant?.bankName || tenant?.bankAccountName) && (
                            <div className="inv-bank-col">
                                <div className="inv-bank-title">Bank Account Details:</div>
                                <div className="inv-bank-grid">
                                    {tenant?.bankName && <><span className="inv-bank-grid-label">Bank:</span><span className="inv-bank-grid-value">{tenant.bankName}</span></>}
                                    {tenant?.bankAccountName && <><span className="inv-bank-grid-label">Account Name:</span><span className="inv-bank-grid-value">{tenant.bankAccountName}</span></>}
                                    {tenant?.bankSortCode && <><span className="inv-bank-grid-label">Sort Code:</span><span className="inv-bank-grid-value">{tenant.bankSortCode}</span></>}
                                    {tenant?.bankAccountNo && <><span className="inv-bank-grid-label">Account No:</span><span className="inv-bank-grid-value">{tenant.bankAccountNo}</span></>}
                                    {tenant?.bankBicCode && <><span className="inv-bank-grid-label">BIC Code:</span><span className="inv-bank-grid-value">{tenant.bankBicCode}</span></>}
                                    {tenant?.bankIban && <><span className="inv-bank-grid-label">IBAN No:</span><span className="inv-bank-grid-value">{tenant.bankIban}</span></>}
                                    {tenant?.bankAddress && <><span className="inv-bank-grid-label">Bank Address:</span><span className="inv-bank-grid-value">{tenant.bankAddress}</span></>}
                                </div>
                                {(tenant?.invoiceFooter || tenant?.invoiceEmail || tenant?.invoiceFax) && (
                                    <div className="inv-remittance">
                                        {tenant?.invoiceFooter && <div className="inv-remittance-note">{tenant.invoiceFooter}</div>}
                                        {(tenant?.invoiceEmail || tenant?.invoiceFax) && (
                                            <div className="inv-remittance-grid">
                                                {tenant?.invoiceEmail && <><span>Email</span><span>{tenant.invoiceEmail}</span></>}
                                                {tenant?.invoiceFax && <><span>Fax</span><span>{tenant.invoiceFax}</span></>}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* ── Transaction Period ── */}
                    <div className="inv-period-row">
                        <span>
                            <strong>Transactions From: </strong>{invoice.transactionFrom}
                            &nbsp;&nbsp;
                            <strong>To: </strong>{invoice.transactionTo}
                        </span>
                        <span><strong>Currency: </strong>{currency}</span>
                    </div>

                    {/* ── Customer + PO row ── */}
                    <div className="inv-customer-row">
                        <span><strong>Customer: </strong>{invoice.customerName}</span>
                        {invoice.poNo && <span><strong>PO No: </strong>{invoice.poNo}</span>}
                    </div>

                    {/* ── Line Items Table ── */}
                    <table className="inv-lines-table">
                        <thead>
                            <tr>
                                <th>Line</th>
                                {isDetailed && <th>Date</th>}
                                <th className="col-item">Duty Type</th>
                                {isDetailed && <th>Guard</th>}
                                {isDetailed && <th>Time</th>}
                                <th>Qty</th>
                                <th className="col-rate">Rate</th>
                                <th className="col-net">Net</th>
                                <th className="col-vat">VAT</th>
                                <th className="col-total">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lineItems.map((line, idx) => (
                                <tr key={idx}>
                                    <td>{idx + 1}</td>
                                    {isDetailed && <td>{line.date || "-"}</td>}
                                    <td className="col-item">
                                        {line.item}
                                        {!!line.dutyCount && <div style={{ fontSize: 11, color: "#9ca3af" }}>{line.dutyCount} {line.dutyCount === 1 ? "duty" : "duties"}</div>}
                                    </td>
                                    {isDetailed && <td>{line.guard || "-"}</td>}
                                    {isDetailed && <td>{line.start || line.finish ? `${line.start || "?"} – ${line.finish || "?"}` : "-"}</td>}
                                    <td>{parseFloat(line.qty || 0).toFixed(2)}</td>
                                    <td className="col-rate">{money(line.rate)}</td>
                                    <td className="col-net">{money(line.net)}</td>
                                    <td className="col-vat">{money(line.vatAmount)}</td>
                                    <td className="col-total">{money(line.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={isDetailed ? 5 : 2} style={{ textAlign: "right" }}>Total:</td>
                                <td>{totalQty.toFixed(2)}</td>
                                <td colSpan={4}></td>
                            </tr>
                        </tfoot>
                    </table>

                    {/* ── Totals Summary ── */}
                    <div className="inv-totals-wrap">
                        <table className="inv-totals-table">
                            <tbody>
                                <tr><td>Net</td><td>{money(invoice.netAmount)}</td></tr>
                                <tr><td>VAT @ {vatRate}%</td><td>{money(invoice.vatAmount)}</td></tr>
                                <tr className="inv-totals-grand"><td>Total</td><td>{money(invoice.totalAmount)}</td></tr>
                            </tbody>
                        </table>
                    </div>

                    {/* ── Footer ── */}
                    {(tenant?.regno || tenant?.legalname || tenant?.name) && (
                        <div className="inv-footer">
                            {tenant?.legalname || tenant?.name}
                            {tenant?.regno && <>. Registered in England and Wales. Company No {tenant.regno}</>}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
